// Accueil : titre et feu de camp, mondes sauvegardés sur cet appareil, création d'un monde (si le serveur l'ouvre) ou
// arrivée dans un monde existant avec son code d'invitation (ou retour de son héros avec son code de retour, ou son
// lien de connexion : ouvert ou collé), puis les cinq règles d'or. Sur iPhone hors écran d'accueil : conseil d'installer Veillée d'abord.
import { useEffect, useState } from "preact/hooks";
import type { TonePreset } from "../../shared/types";
import { lireConnexion, normaliserRetour, RETOUR_LENGTH } from "../../shared/retour";
import { creationRights, createWorld, joinWorld, listSessions, removeSession, resumeWorld } from "../api";
import type { ConteurKind } from "../../shared/chatgpt";
import type { Session } from "../api";
import { etatNotifications } from "../components/NotifyButton";
import { connect } from "../store";
import "./Home.css";

type Tab = "creer" | "rejoindre";

interface ToneCard { id: TonePreset; icon: string; label: string; hint: string }

const TONES: ToneCard[] = [
  { id: "heroique", icon: "⚔️", label: "Héroïque", hint: "Serments, batailles et bannières qui claquent au vent." },
  { id: "sombre", icon: "🌑", label: "Sombre", hint: "Un monde qui ne pardonne rien. La lumière se mérite." },
  { id: "feerique", icon: "🧚", label: "Féérique", hint: "Forêts qui parlent, fées capricieuses et pactes anciens." },
  { id: "humoristique", icon: "🎭", label: "Loufoque", hint: "Quiproquos, aubergistes farfelus et catastrophes joyeuses." },
  { id: "steampunk", icon: "⚙️", label: "Steampunk", hint: "Vapeur, engrenages, dirigeables et inventeurs un peu fous." },
  { id: "pirates", icon: "🏴‍☠️", label: "Pirates", hint: "Mers ouvertes, trésors enfouis et équipages en mal de gloire." },
  { id: "post_apo", icon: "☢️", label: "Post-apocalypse", hint: "Après la chute : survivre, reconstruire, ou simplement tenir." },
  { id: "libre", icon: "✒️", label: "Libre", hint: "Le Conteur improvise à partir de ton idée, sans ton imposé." },
];

const IDEA_EXAMPLES = [
  "une famille de pêcheurs entend la mer lui parler la nuit…",
  "un cirque itinérant qui cache un secret sous son chapiteau…",
  "des naufragés échoués sur une île que nulle carte ne montre…",
];

const RULES: { title: string; text: string }[] = [
  { title: "Le monde vit en temps réel.", text: "Il suit l’heure de Paris, jour et nuit. Quand tu dors, les autres avancent, et l’histoire continue sans toi." },
  { title: "Agis au moins une fois par jour.", text: "Chaque journée sans geste de ta part éteint une flamme. Quand elles sont toutes éteintes, ton personnage s’égare et erre dans le récit des autres." },
  { title: "Le Conteur te pose des questions.", text: "Certaines ont une échéance. Sans réponse à temps, l’option par défaut se joue toute seule, et le monde en garde la trace." },
  { title: "Mange, dors, soigne-toi.", text: "La faim, la fatigue et les blessures comptent, même quand tu n’es pas là." },
  { title: "Vous commencez loin les uns des autres.", text: "Il faudra des routes, des rencontres et un peu de chance pour vous retrouver." },
];

const CODE_LENGTH = 6;
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 20;
const DEFAULT_PLAYERS = 4;

/** Code d'invitation : majuscules, lettres et chiffres seulement, 6 caractères au plus. */
function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, CODE_LENGTH);
}

/** Code d'invitation présent dans l'URL (?code=XXXXXX), normalisé ; vide sinon. */
export function readInviteCode(): string {
  return normalizeCode(new URLSearchParams(window.location.search).get("code") ?? "");
}

/** Lien de connexion présent dans l'URL (#connexion=…) : code du monde et code de retour ; null sinon. */
export function readConnexionLink(): { code: string; retour: string } | null {
  return window.location.hash ? lireConnexion(window.location.hash) : null;
}

/** « ABCDEFGH » → « ABCD-EFGH », comme dans l'onglet Compagnons. */
function showRetour(retour: string): string {
  return `${retour.slice(0, 4)}-${retour.slice(4)}`;
}

function messageOf(err: unknown): string {
  return err instanceof Error && err.message ? err.message : "Le feu s’est éteint. Réessaie dans un instant.";
}

function clampPlayers(n: number): number {
  return Math.min(MAX_PLAYERS, Math.max(MIN_PLAYERS, n));
}

const SPARKS = [
  { x: -22, delay: 0, life: 2.2 },
  { x: 12, delay: 0.7, life: 2.6 },
  { x: 26, delay: 1.3, life: 2.0 },
  { x: -6, delay: 1.9, life: 2.4 },
  { x: 4, delay: 0.4, life: 3.0 },
];

/** Feu de camp décoratif (SVG + CSS) : bûches, trois langues de flamme, étincelles. Sert aussi à l'écran de connexion. */
export function Campfire({ size = 140 }: { size?: number }) {
  return (
    <div class="campfire" style={`width:${size}px;height:${size}px`} aria-hidden="true">
      <span class="campfire-halo" />
      <svg class="campfire-svg" viewBox="0 0 120 120" focusable="false">
        <g class="campfire-logs">
          <rect x="22" y="96" width="76" height="13" rx="6.5" transform="rotate(-10 60 102)" />
          <rect x="22" y="96" width="76" height="13" rx="6.5" transform="rotate(10 60 102)" />
        </g>
        <path
          class="campfire-flame campfire-flame--outer"
          d="M60 8C74 28 92 44 91 72 90 95 76 106 60 106 44 106 30 95 29 72 28 52 40 42 46 24 52 35 55 39 60 41 58 29 58 18 60 8Z"
        />
        <path
          class="campfire-flame campfire-flame--mid"
          d="M60 36C68 50 82 60 81 80 80 94 71 102 60 102 49 102 40 94 39 80 38 66 49 58 52 48 55 54 57 57 60 58 59 50 59 42 60 36Z"
        />
        <path
          class="campfire-flame campfire-flame--core"
          d="M60 68C64 76 70 82 69 92 68 99 64 102 60 102 56 102 52 99 51 92 50 82 56 78 60 68Z"
        />
      </svg>
      {SPARKS.map((s, i) => (
        <span key={i} class="campfire-spark" style={`--x:${s.x}px;--d:${s.delay}s;--t:${s.life}s`} />
      ))}
    </div>
  );
}

export function Home() {
  const [link] = useState(readConnexionLink);
  const [invite] = useState(readInviteCode);
  const [saved, setSaved] = useState<Session[]>(() => listSessions());
  const [tab, setTab] = useState<Tab>(invite || link ? "rejoindre" : "creer");
  const [code, setCode] = useState(link?.code ?? invite);
  const [playerName, setPlayerName] = useState(() => saved[0]?.playerName ?? "");
  const [worldName, setWorldName] = useState("");
  const [pitch, setPitch] = useState("");
  const [tone, setTone] = useState<TonePreset>("heroique");
  const [players, setPlayers] = useState(DEFAULT_PLAYERS);
  const [example] = useState(() => IDEA_EXAMPLES[Math.floor(Math.random() * IDEA_EXAMPLES.length)]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const [howOpen, setHowOpen] = useState(false);
  const [returning, setReturning] = useState(link !== null);
  const [recovery, setRecovery] = useState(link ? showRetour(link.retour) : "");
  // Sur iPhone, l'icône de l'écran d'accueil ne partage pas la mémoire de Safari : mieux vaut installer avant d'entrer.
  const [mustInstall] = useState(() => etatNotifications() === "installer");
  // Onglet Créer caché tant que le serveur n'a pas dit quels mondes on peut créer (Claude offert, ou son ChatGPT).
  const [rights, setRights] = useState<Record<ConteurKind, boolean>>({ claude: false, chatgpt: false });
  const [conteur, setConteur] = useState<ConteurKind>("chatgpt");
  const canCreate = rights.claude || rights.chatgpt;

  useEffect(() => {
    let alive = true;
    void creationRights().then((r) => {
      if (!alive) return;
      setRights(r);
      setConteur(r.chatgpt ? "chatgpt" : "claude");
    });
    return () => { alive = false; };
  }, []);

  // Lien de connexion : retiré de l'adresse (il ne doit pas traîner dans l'historique), puis on retrouve le héros.
  useEffect(() => {
    if (!link) return;
    history.replaceState(null, "", location.pathname + location.search);
    void enter(() => resumeWorld(link.code, link.retour));
  }, []);

  const creating = canCreate && tab === "creer";
  const selected = TONES.find((t) => t.id === tone) ?? TONES[0];
  let submitLabel = creating ? "Allumer ce monde" : returning ? "Retrouver mon héros" : "Rejoindre la veillée";
  if (busy) submitLabel = creating ? "Le Conteur tisse le monde…" : "Un instant…";

  function switchTab(next: Tab): void {
    setTab(next);
    setError(null);
  }

  /** Un lien (ou une clé) de connexion collé dans l'un des deux codes remplit le code du monde et le code de retour. */
  function pasteConnexion(e: ClipboardEvent): void {
    const found = lireConnexion(e.clipboardData?.getData("text") ?? "");
    if (!found) return;
    e.preventDefault();
    setCode(found.code);
    setRecovery(showRetour(found.retour));
    setReturning(true);
    setError(null);
  }

  /** Oublier un monde ne fait que le retirer de cet appareil ; il reste accessible avec son code. */
  function forget(target: string): void {
    removeSession(target);
    setSaved(listSessions());
    setForgetting(null);
  }

  /** Crée ou rejoint un monde ; en cas de succès, connect ouvre la veillée. */
  async function enter(task: () => Promise<Session>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      connect(await task());
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: Event): void {
    e.preventDefault();
    if (busy) return;
    const me = playerName.trim();
    if (creating) {
      const name = worldName.trim();
      if (!name) {
        setError("Donne un nom à ton monde.");
        return;
      }
      if (!me) {
        setError("Quel est ton prénom ?");
        return;
      }
      void enter(() => createWorld({ worldName: name, pitch: pitch.trim(), tone, playerName: me, expectedPlayers: players, conteur }));
      return;
    }
    const target = normalizeCode(code);
    setCode(target);
    if (target.length !== CODE_LENGTH) {
      setError("Le code d’invitation fait 6 caractères.");
      return;
    }
    if (returning) {
      const secret = normaliserRetour(recovery);
      if (secret.length !== RETOUR_LENGTH) {
        setError("Le code de retour fait 8 caractères, par exemple ABCD-EFGH.");
        return;
      }
      void enter(() => resumeWorld(target, secret));
      return;
    }
    if (!me) {
      setError("Quel est ton prénom ?");
      return;
    }
    void enter(() => joinWorld(target, me));
  }

  return (
    <main class="home">
      <header class="home-hero">
        <Campfire />
        <h1 class="home-title">Veillée</h1>
        <p class="home-tagline">Un monde qui vit même quand tu dors. Un Conteur qui se souvient de tout.</p>
      </header>

      {saved.length > 0 && (
        <section class="home-section" aria-labelledby="home-saved-title">
          <h2 id="home-saved-title" class="home-section-title">Tes mondes</h2>
          <p class="home-hint">Enregistrés sur cet appareil uniquement.</p>
          <ul class="home-worlds">
            {saved.map((s) => (
              <li key={s.code} class="card home-world">
                <div class="home-world-head">
                  <span class="home-world-name">{s.worldName}</span>
                  <span class="home-world-code">{s.code}</span>
                </div>
                <p class="home-world-meta">
                  Tu y joues sous le nom de <strong>{s.playerName}</strong>
                </p>
                {forgetting === s.code ? (
                  <div class="home-world-actions">
                    <p class="home-world-ask">Oublier ce monde sur cet appareil ? Tu pourras le rejoindre avec son code.</p>
                    <button type="button" class="btn btn-danger btn-small" onClick={() => forget(s.code)}>Oublier</button>
                    <button type="button" class="btn btn-ghost btn-small" onClick={() => setForgetting(null)}>Garder</button>
                  </div>
                ) : (
                  <div class="home-world-actions">
                    <button type="button" class="btn btn-primary" onClick={() => connect(s)}>Reprendre</button>
                    <button type="button" class="btn btn-ghost btn-small" onClick={() => setForgetting(s.code)}>Oublier</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section class="home-section" aria-label="Créer ou rejoindre un monde">
        {mustInstall && (
          <div class="card home-install" role="note">
            <span class="home-install-icon" aria-hidden="true">📲</span>
            <p class="home-install-text">
              Sur iPhone, installe d’abord Veillée : touche Partager ⬆️ puis « Sur l’écran d’accueil », et ouvre-la depuis l’icône
              pour {canCreate ? "créer ou rejoindre" : "rejoindre"} ton monde. Ce que tu fais ici, dans Safari, n’y sera pas.
              {invite !== "" && <> Ton code d’invitation : <strong class="home-install-code">{invite}</strong>.</>}
            </p>
          </div>
        )}
        {!canCreate && (
          <p class="home-hint home-closed">Pour l’instant, on ne crée pas de nouveau monde : entre le code d’invitation qu’on t’a donné.</p>
        )}
        {canCreate && <div class="home-tabs" role="tablist" aria-label="Créer ou rejoindre un monde">
          <button
            type="button"
            role="tab"
            id="home-tab-creer"
            class={`home-tab${creating ? " is-active" : ""}`}
            aria-selected={creating ? "true" : "false"}
            aria-controls="home-form"
            onClick={() => switchTab("creer")}
          >
            Créer
          </button>
          <button
            type="button"
            role="tab"
            id="home-tab-rejoindre"
            class={`home-tab${creating ? "" : " is-active"}`}
            aria-selected={creating ? "false" : "true"}
            aria-controls="home-form"
            onClick={() => switchTab("rejoindre")}
          >
            Rejoindre
          </button>
        </div>}

        <div id="home-form" role={canCreate ? "tabpanel" : undefined} aria-labelledby={canCreate ? (creating ? "home-tab-creer" : "home-tab-rejoindre") : undefined}>
          <form class="card home-form" onSubmit={onSubmit}>
            {creating ? (
              <>
                <div class="home-field">
                  <label class="label" for="home-world-name">Nom du monde</label>
                  <input
                    id="home-world-name"
                    class="input"
                    type="text"
                    autoComplete="off"
                    maxLength={60}
                    placeholder="Les Cendres de Valmont"
                    value={worldName}
                    onInput={(e) => setWorldName(e.currentTarget.value)}
                  />
                </div>

                <div class="home-field">
                  <span class="label" id="home-tone-label">Le ton du monde</span>
                  <div class="home-tones" role="radiogroup" aria-labelledby="home-tone-label">
                    {TONES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        role="radio"
                        class="home-tone"
                        aria-checked={tone === t.id ? "true" : "false"}
                        onClick={() => setTone(t.id)}
                      >
                        <span class="home-tone-icon" aria-hidden="true">{t.icon}</span>
                        <span class="home-tone-label">{t.label}</span>
                      </button>
                    ))}
                  </div>
                  <p class="home-tone-hint">{selected.hint}</p>
                </div>

                <div class="home-field">
                  <label class="label" for="home-pitch">
                    Ton idée <span class="muted">(facultatif)</span>
                  </label>
                  <textarea
                    id="home-pitch"
                    class="textarea"
                    rows={4}
                    maxLength={600}
                    placeholder={`Ex. : ${example}`}
                    value={pitch}
                    onInput={(e) => setPitch(e.currentTarget.value)}
                  />
                </div>

                <div class="home-field">
                  <span class="label" id="home-conteur-label">Le Conteur</span>
                  {rights.claude && rights.chatgpt ? (
                    <div class="home-conteurs" role="radiogroup" aria-labelledby="home-conteur-label">
                      {(["chatgpt", "claude"] as const).map((k) => (
                        <button key={k} type="button" role="radio" class="home-conteur" aria-checked={conteur === k ? "true" : "false"} onClick={() => setConteur(k)}>
                          {k === "chatgpt" ? "Ton compte ChatGPT" : "Claude, offert par Veillée"}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <p class="home-hint">
                    {conteur === "chatgpt"
                      ? "Le Conteur écrira avec ton propre compte ChatGPT (ton abonnement, même gratuit) : tu le connecteras juste après, en tapant un code sur le site d’OpenAI."
                      : "Le Conteur écrira avec Claude, offert par Veillée."}
                  </p>
                </div>

                <div class="home-field">
                  <span class="label" id="home-players-label">Joueurs prévus</span>
                  <div class="home-stepper" role="group" aria-labelledby="home-players-label">
                    <button
                      type="button"
                      class="icon-btn home-step"
                      aria-label="Un joueur de moins"
                      disabled={players <= MIN_PLAYERS}
                      onClick={() => setPlayers(clampPlayers(players - 1))}
                    >
                      −
                    </button>
                    <span class="home-stepper-value" aria-live="polite">{players}</span>
                    <button
                      type="button"
                      class="icon-btn home-step"
                      aria-label="Un joueur de plus"
                      disabled={players >= MAX_PLAYERS}
                      onClick={() => setPlayers(clampPlayers(players + 1))}
                    >
                      +
                    </button>
                  </div>
                  <p class="home-hint">Une estimation : d’autres pourront arriver plus tard.</p>
                </div>
              </>
            ) : (
              <div class="home-field">
                <label class="label" for="home-code">Code d’invitation</label>
                <input
                  id="home-code"
                  class="input home-code-input"
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  autoComplete="off"
                  spellcheck={false}
                  maxLength={CODE_LENGTH}
                  placeholder="ABC123"
                  value={code}
                  onInput={(e) => setCode(normalizeCode(e.currentTarget.value))}
                  onPaste={pasteConnexion}
                />
                {invite !== "" && code === invite && (
                  <p class="home-hint">Ce code vient de ton lien d’invitation.</p>
                )}
              </div>
            )}

            {creating || !returning ? (
              <div class="home-field">
                <label class="label" for="home-player">Ton prénom</label>
                <input
                  id="home-player"
                  class="input"
                  type="text"
                  autoComplete="given-name"
                  maxLength={24}
                  placeholder="Comment te nomme-t-on ?"
                  value={playerName}
                  onInput={(e) => setPlayerName(e.currentTarget.value)}
                />
              </div>
            ) : (
              <div class="home-field">
                <label class="label" for="home-recovery">Ton code de retour</label>
                <input
                  id="home-recovery"
                  class="input home-code-input"
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  autoComplete="off"
                  spellcheck={false}
                  maxLength={12}
                  placeholder="ABCD-EFGH"
                  value={recovery}
                  onInput={(e) => setRecovery(e.currentTarget.value.toUpperCase())}
                  onPaste={pasteConnexion}
                />
                <p class="home-hint">
                  Tu as un lien de connexion ? Colle-le ici. Sinon, ton code de retour est dans l’onglet Compagnons de l’appareil où
                  tu as créé ton héros.
                </p>
              </div>
            )}

            {!creating && (
              <button
                type="button"
                class="btn btn-ghost btn-small home-returning-toggle"
                onClick={() => {
                  setReturning((r) => !r);
                  setError(null);
                }}
              >
                {returning ? "Je suis nouveau dans ce monde" : "J’ai déjà un héros ici"}
              </button>
            )}

            {error && <p class="home-error" role="alert">{error}</p>}

            <button type="submit" class="btn btn-primary home-submit" disabled={busy}>
              {submitLabel}
            </button>
          </form>
        </div>
      </section>

      <section class="home-how" aria-label="Aide">
        <button
          type="button"
          class="home-how-toggle"
          aria-expanded={howOpen ? "true" : "false"}
          aria-controls="home-how-rules"
          onClick={() => setHowOpen((open) => !open)}
        >
          Comment ça marche ?
          <span class="home-how-chevron" aria-hidden="true">▾</span>
        </button>
        {howOpen && (
          <ol id="home-how-rules" class="card-parchment home-rules">
            {RULES.map((r) => (
              <li key={r.title} class="home-rule">
                <p class="home-rule-text">
                  <strong>{r.title}</strong> {r.text}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}
